<template>
  <div class="dashboard-stats-row">
    <!-- LEFT: Suivi colis instantané -->
    <div class="stat-panel suivi-panel">
      <div class="panel-header">
        <div class="header-icon">
          <feather-icon
            icon="PackageIcon"
            size="18"
          />
        </div>
        <div class="header-text">
          <h3>Suivi colis instantané</h3>
          <span class="header-date">{{ dashboardData.date || getCurrentDate() }}</span>
        </div>
      </div>

      <!-- Date Filter Row -->
      <div class="date-filter-inline">
        <div class="filter-item">
          <label>Du:</label>
          <b-form-datepicker
            v-model="startDate"
            size="sm"
            local="fr"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            @input="fetchInstantDashboard"
          />
        </div>
        <div class="filter-item">
          <label>Au:</label>
          <b-form-datepicker
            v-model="endDate"
            size="sm"
            local="fr"
            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
            @input="fetchInstantDashboard"
          />
        </div>
      </div>

      <!-- Total Amount Box -->
      <div class="total-box">
        <span class="total-amount">{{ formatCurrency(dashboardData.total_ttc || 0) }}</span>
        <span class="total-label">Montant en cours</span>
      </div>

      <!-- Status Circles Grid (2x2) -->
      <div class="circles-grid">
        <div
          v-for="statusItem in getGridStatuses()"
          :key="statusItem.status"
          class="circle-card"
          :class="getStatusClass(statusItem.status)"
          @click="navigateToOrders(statusItem.status)"
        >
          <div class="circle">
            <span class="circle-count">{{ statusItem.count }}</span>
          </div>
          <span class="circle-label">{{ statusItem.label }}</span>
        </div>
      </div>
    </div>

    <!-- MIDDLE: Rapport de prochain paiement -->
    <div class="stat-panel rapport-panel">
      <h3 class="rapport-title">
        Rapport de prochain paiement
      </h3>

      <div class="rapport-rows">
        <div class="rapport-row">
          <span class="row-label">Nb. Total colis :</span>
          <span class="row-value badge-gray">{{ rapportData.totalColis }} colis</span>
        </div>
        <div class="rapport-row">
          <span class="row-label">Montant total :</span>
          <span class="row-value text-primary">{{ formatCurrency(rapportData.montantTotal) }}</span>
        </div>
        <div class="rapport-row">
          <span class="row-label">Nb. colis livré :</span>
          <span class="row-value badge-gray">{{ rapportData.colisLivre }} colis</span>
        </div>
        <div class="rapport-row">
          <span class="row-label">Frais livraison :</span>
          <span class="row-value text-primary">{{ formatCurrency(rapportData.fraisLivraison) }}</span>
        </div>
        <div class="rapport-row">
          <span class="row-label">Nb. colis retour :</span>
          <span class="row-value badge-gray">{{ rapportData.colisRetour }} colis</span>
        </div>
        <div class="rapport-row">
          <span class="row-label">Frais retour :</span>
          <span class="row-value text-primary">{{ formatCurrency(rapportData.fraisRetour) }}</span>
        </div>
        <div class="rapport-row total-row">
          <span class="row-label"><strong>Total à encaisser :</strong></span>
          <span class="row-value text-success">{{ formatCurrency(rapportData.totalEncaisser) }}</span>
        </div>
      </div>
    </div>

    <!-- RIGHT: Performance Chart -->
    <div class="stat-panel performance-panel">
      <div class="performance-header">
        <h3>Total : {{ performanceData.total }} colis</h3>
        <span class="date-range">{{ getDateRange() }}</span>
      </div>

      <!-- Donut Chart -->
      <div class="donut-chart-container">
        <div class="donut-chart">
          <svg viewBox="0 0 100 100">
            <circle
              class="donut-ring"
              cx="50"
              cy="50"
              r="40"
              fill="transparent"
              stroke="#e8e8e8"
              stroke-width="12"
            />
            <circle
              class="donut-segment delivered"
              cx="50"
              cy="50"
              r="40"
              fill="transparent"
              stroke="#28a745"
              stroke-width="12"
              :stroke-dasharray="getDeliveredDashArray()"
              stroke-dashoffset="0"
              transform="rotate(-90 50 50)"
            />
            <circle
              class="donut-segment returned"
              cx="50"
              cy="50"
              r="40"
              fill="transparent"
              stroke="#dc3545"
              stroke-width="12"
              :stroke-dasharray="getReturnedDashArray()"
              :stroke-dashoffset="getReturnedOffset()"
              transform="rotate(-90 50 50)"
            />
          </svg>
          <div class="donut-center">
            <span class="donut-percentage">{{ performanceData.percentage }}%</span>
            <span class="donut-label">Performance</span>
          </div>
        </div>
      </div>

      <!-- Stats Boxes -->
      <div class="stats-boxes">
        <div class="stats-box delivered-box">
          <span class="box-title">Livrée</span>
          <span class="box-count">{{ performanceData.delivered }} colis</span>
          <span class="box-percentage">{{ performanceData.deliveredPercent }}%</span>
        </div>
        <div class="stats-box returned-box">
          <span class="box-title">Retour</span>
          <span class="box-count">{{ performanceData.returned }} colis</span>
          <span class="box-percentage">{{ performanceData.returnedPercent }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import storeAuth from '@/store/store'
import dayjs from 'dayjs'
import {
  BFormDatepicker,
} from 'bootstrap-vue'

export default {
  name: 'InstantDashboard',
  components: {
    BFormDatepicker,
  },
  data() {
    return {
      startDate: dayjs().format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      dashboardData: {
        date: '',
        total_ttc: 0,
        statuses: [],
      },
      rapportData: {
        totalColis: 0,
        montantTotal: 0,
        colisLivre: 0,
        fraisLivraison: 0,
        colisRetour: 0,
        fraisRetour: 0,
        totalEncaisser: 0,
      },
      performanceData: {
        total: 0,
        percentage: 0,
        delivered: 0,
        deliveredPercent: 0,
        returned: 0,
        returnedPercent: 0,
      },
      isLoading: false,
      user: {},
    }
  },
  created() {
    this.user = storeAuth.state.user
    this.fetchInstantDashboard()
  },
  methods: {
    async fetchInstantDashboard() {
      this.isLoading = true
      try {
        const params = {
          start_date: this.startDate,
          end_date: this.endDate,
        }
        if (this.user.role === 'supplier') {
          params.supplier = this.user.id
        }
        if (this.user.repository) {
          params.repository = this.user.repository
        }

        const response = await axios.get('/api/orders/instant-dashboard/', { params })
        this.dashboardData = response.data

        // Use payment_report data from API if available
        if (response.data.payment_report) {
          this.rapportData = {
            totalColis: response.data.payment_report.total_colis,
            montantTotal: response.data.payment_report.montant_total,
            colisLivre: response.data.payment_report.colis_livre,
            fraisLivraison: response.data.payment_report.frais_livraison,
            colisRetour: response.data.payment_report.colis_retour,
            fraisRetour: response.data.payment_report.frais_retour,
            totalEncaisser: response.data.payment_report.total_encaisser,
          }

          // Calculate performance data from payment_report
          const totalProcessed = response.data.payment_report.colis_livre + response.data.payment_report.colis_retour
          this.performanceData = {
            total: totalProcessed,
            percentage: totalProcessed > 0 ? ((response.data.payment_report.colis_livre / totalProcessed) * 100).toFixed(2) : 0,
            delivered: response.data.payment_report.colis_livre,
            deliveredPercent: totalProcessed > 0 ? ((response.data.payment_report.colis_livre / totalProcessed) * 100).toFixed(2) : 0,
            returned: response.data.payment_report.colis_retour,
            returnedPercent: totalProcessed > 0 ? ((response.data.payment_report.colis_retour / totalProcessed) * 100).toFixed(2) : 0,
          }
        }
      } catch (error) {
        console.error('Error fetching instant dashboard:', error)
      } finally {
        this.isLoading = false
      }
    },

    getStatusByCode(code) {
      const status = this.dashboardData.statuses?.find(s => s.status === code)
      return status || { count: 0, total_ttc: 0 }
    },

    getGridStatuses() {
      // Return all 5 statuses for the grid (en cours, livré, livré temporaire, retour temporaire, retour au dépot)
      const gridOrder = [3, 4, 19, 20, 5]
      return gridOrder.map(code => {
        const status = this.getStatusByCode(code)
        const labels = {
          3: 'En cours',
          4: 'Livré',
          19: 'Livré Temporaire',
          20: 'Retour Temporaire',
          5: 'Retour au dépot',
        }
        return {
          status: code,
          count: status.count,
          label: labels[code],
        }
      })
    },

    getCurrentDate() {
      if (this.startDate === this.endDate) {
        return dayjs(this.startDate).format('D MMMM YYYY')
      }
      return `${dayjs(this.startDate).format('D MMMM YYYY')} - ${dayjs(this.endDate).format('D MMMM YYYY')}`
    },

    getDateRange() {
      const start = dayjs(this.startDate).format('DD/MM/YYYY')
      const end = dayjs(this.endDate).format('DD/MM/YYYY')
      return `${start} - ${end}`
    },

    formatCurrency(amount) {
      const numericAmount = parseFloat(amount) || 0
      return `${new Intl.NumberFormat('fr-FR', {
        style: 'decimal',
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(numericAmount)} DT`
    },

    getStatusClass(status) {
      const classes = {
        3: 'status-en-cours',
        4: 'status-livre',
        19: 'status-livre-temp',
        20: 'status-retour-temp',
        5: 'status-retour',
      }
      return classes[status] || ''
    },

    getDeliveredDashArray() {
      const circumference = 2 * Math.PI * 40
      const percent = parseFloat(this.performanceData.deliveredPercent) || 0
      return `${(percent / 100) * circumference} ${circumference}`
    },

    getReturnedDashArray() {
      const circumference = 2 * Math.PI * 40
      const percent = parseFloat(this.performanceData.returnedPercent) || 0
      return `${(percent / 100) * circumference} ${circumference}`
    },

    getReturnedOffset() {
      const circumference = 2 * Math.PI * 40
      const deliveredPercent = parseFloat(this.performanceData.deliveredPercent) || 0
      return `-${(deliveredPercent / 100) * circumference}`
    },

    navigateToOrders(status) {
      this.$router.push({
        name: 'order-list',
        params: { level: status.toString() },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-stats-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.stat-panel {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

// ====== LEFT PANEL: Suivi colis ======
.suivi-panel {
  .panel-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    .header-icon {
      width: 36px;
      height: 36px;
      background: #1a1a2e;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
    }

    .header-text {
      h3 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: #1a1a2e;
      }

      .header-date {
        font-size: 0.8rem;
        color: #6c757d;
      }
    }
  }

  .total-box {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-radius: 10px;
    padding: 1rem;
    text-align: center;
    margin-bottom: 1.25rem;
    border: 2px dashed #dee2e6;

    .total-amount {
      display: block;
      font-size: 1.75rem;
      font-weight: 700;
      color: #28a745;
    }

    .total-label {
      display: block;
      font-size: 0.85rem;
      color: #ff6b35;
      font-weight: 500;
      margin-top: 0.25rem;
    }
  }

  .date-filter-inline {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
    background: #f8f9fa;
    padding: 0.5rem;
    border-radius: 8px;
    border: 1px solid #e9ecef;

    .filter-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1;

      label {
        margin: 0;
        font-size: 0.8rem;
        font-weight: 600;
        color: #495057;
      }

      .b-form-datepicker {
        flex: 1;
      }
    }
  }

  .circles-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.75rem;
  }

  .circle-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.05);
    }

    .circle {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid;
      margin-bottom: 0.5rem;

      .circle-count {
        font-size: 1.25rem;
        font-weight: 700;
      }
    }

    .circle-label {
      font-size: 0.8rem;
      font-weight: 600;
      text-align: center;
    }

    &.status-en-cours {
      .circle {
        border-color: #17a2b8;
        background: rgba(23, 162, 184, 0.1);
        .circle-count { color: #17a2b8; }
      }
      .circle-label { color: #17a2b8; }
    }

    &.status-livre {
      .circle {
        border-color: #28a745;
        background: rgba(40, 167, 69, 0.1);
        .circle-count { color: #28a745; }
      }
      .circle-label { color: #28a745; }
    }

    &.status-livre-temp {
      .circle {
        border-color: #667eea;
        background: rgba(102, 126, 234, 0.1);
        .circle-count { color: #667eea; }
      }
      .circle-label { color: #667eea; }
    }

    &.status-retour-temp {
      .circle {
        border-color: #6c757d;
        background: rgba(108, 117, 125, 0.1);
        .circle-count { color: #6c757d; }
      }
      .circle-label { color: #6c757d; }
    }

    &.status-retour {
      .circle {
        border-color: #ff9800;
        background: rgba(255, 152, 0, 0.1);
        .circle-count { color: #ff9800; }
      }
      .circle-label { color: #ff9800; }
    }
  }
}

// ====== MIDDLE PANEL: Rapport ======
.rapport-panel {
  .rapport-title {
    color: #17a2b8;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #f0f0f0;
  }

  .rapport-rows {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .rapport-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.35rem 0;

    .row-label {
      font-size: 0.85rem;
      color: #495057;
    }

    .row-value {
      font-size: 0.85rem;
      font-weight: 600;

      &.badge-gray {
        background: #e9ecef;
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        color: #495057;
      }

      &.text-primary {
        color: #17a2b8;
      }

      &.text-success {
        color: #28a745;
        font-size: 1rem;
      }
    }

    &.total-row {
      margin-top: 0.5rem;
      padding-top: 0.75rem;
      border-top: 2px solid #f0f0f0;
    }
  }
}

// ====== RIGHT PANEL: Performance ======
.performance-panel {
  .performance-header {
    text-align: center;
    margin-bottom: 1rem;

    h3 {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #1a1a2e;
    }

    .date-range {
      font-size: 0.8rem;
      color: #6c757d;
    }
  }

  .donut-chart-container {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .donut-chart {
    position: relative;
    width: 150px;
    height: 150px;

    svg {
      width: 100%;
      height: 100%;
    }

    .donut-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;

      .donut-percentage {
        display: block;
        font-size: 1.5rem;
        font-weight: 700;
        color: #28a745;
      }

      .donut-label {
        display: block;
        font-size: 0.75rem;
        color: #ff9800;
        font-weight: 500;
      }
    }
  }

  .stats-boxes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }

  .stats-box {
    padding: 0.75rem;
    border-radius: 8px;
    text-align: center;

    .box-title {
      display: block;
      font-size: 0.9rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
    }

    .box-count {
      display: block;
      font-size: 1.1rem;
      font-weight: 700;
    }

    .box-percentage {
      display: block;
      font-size: 0.85rem;
      font-weight: 600;
    }

    &.delivered-box {
      background: rgba(40, 167, 69, 0.1);
      .box-title { color: #28a745; }
      .box-count { color: #28a745; }
      .box-percentage { color: #28a745; }
    }

    &.returned-box {
      background: rgba(220, 53, 69, 0.1);
      .box-title { color: #dc3545; }
      .box-count { color: #dc3545; }
      .box-percentage { color: #dc3545; }
    }
  }
}
</style>
